import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Lightning, Rocket, Sparkle, X } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { fmtET, etTimeMs, etWallString } from "../lib/etTime";

/**
 * Pro-only boost scheduler. Owner picks an upcoming event of theirs and a
 * duration (6 / 8 / 10 / 12 hours, capped to event duration). Backend
 * validates: 6–12h range, must overlap an event, can't extend past event
 * end, max 2 active in a rolling 7-day window.
 *
 * For non-Pro owners: shows a soft locked teaser instead.
 */
export default function BoostCard() {
  const { user } = useAuth();
  const [boosts, setBoosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [eventId, setEventId] = useState("");
  const [durationH, setDurationH] = useState(6);

  const isPro = !!user?.is_pro;

  const reload = async () => {
    setLoading(true);
    try {
      const [boostsRes, todayRes, weekRes, nextWeekRes] = await Promise.all([
        api.get("/boosts"),
        api.get("/trucks/upcoming?when=today"),
        api.get("/trucks/upcoming?when=week"),
        api.get("/trucks/upcoming?when=next_week"),
      ]);
      setBoosts(boostsRes.data || []);
      const all = [...todayRes.data, ...weekRes.data, ...nextWeekRes.data];
      // Owner-only: filter to their own events that haven't ended
      const mine = all.filter(
        (e) => e.owner_id === user?.id && new Date(e.ends_at).getTime() > Date.now(),
      );
      // De-dupe by id and sort soonest-first
      const seen = new Set();
      const unique = mine
        .filter((e) => {
          if (seen.has(e.id)) return false;
          seen.add(e.id);
          return true;
        })
        .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
      setEvents(unique);
      if (unique.length && !eventId) setEventId(unique[0].id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load boost data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isPro) {
      setLoading(false);
      return;
    }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro]);

  if (!isPro) {
    return (
      <section
        className="rounded-2xl border border-dashed border-primary/40 bg-primary/5 p-5"
        data-testid="boost-locked"
      >
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-bold text-primary">
          <Sparkle weight="fill" className="h-3.5 w-3.5" />
          Pro feature
        </div>
        <h3 className="font-display font-semibold text-lg mt-1 flex items-center gap-2">
          <Rocket weight="duotone" className="h-5 w-5 text-primary" />
          Featured boost
        </h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-md">
          Boost an upcoming event to get a featured pin and top placement in the customer list. Up to 2 boosts per week, up to 12h each (capped to event length).
        </p>
      </section>
    );
  }

  // Active+upcoming boosts within last 7 days for the quota readout
  const now = Date.now();
  const recent = boosts.filter((b) => new Date(b.starts_at).getTime() > now - 7 * 24 * 3600 * 1000);
  const remaining = Math.max(0, 2 - recent.length);

  const selectedEvent = events.find((e) => e.id === eventId);
  // Cap duration to event length. New rules: any positive duration up to
  // min(12h, event length). Discrete picker offers common slots, plus we
  // always include the event's full length as an option so the owner can
  // boost the whole window in one tap.
  const eventLengthH = selectedEvent
    ? (new Date(selectedEvent.ends_at) - new Date(selectedEvent.starts_at)) / 3600000
    : 0;
  const baseChoices = [1, 2, 3, 4, 6, 8, 12];
  // Round event length to one decimal so 2.5h events show "2.5h" cleanly.
  const eventChoice = eventLengthH > 0 ? Math.round(eventLengthH * 10) / 10 : 0;
  const cap = Math.min(12, eventLengthH);
  const validDurations = Array.from(
    new Set([
      ...baseChoices.filter((d) => d <= cap),
      ...(eventChoice > 0 && eventChoice <= 12 ? [eventChoice] : []),
    ]),
  ).sort((a, b) => a - b);
  // Default to the longest valid choice (most coverage)
  const effectiveDuration = validDurations.includes(durationH)
    ? durationH
    : (validDurations[validDurations.length - 1] || null);

  const submit = async () => {
    if (!selectedEvent || !effectiveDuration) {
      toast.error("Pick an event and a duration first");
      return;
    }
    setSubmitting(true);
    try {
      // Schedule events come back with naive ET wall-clock strings (e.g.
      // "2026-05-02T10:00:00" meaning 10am ET). The backend's overlap
      // check rebuilds the schedule entry in the same naive-ET frame, so
      // we MUST send both timestamps back in that frame. etTimeMs/
      // etWallString make this correct on any device timezone.
      const startMs = etTimeMs(selectedEvent.starts_at);
      const endsMs = startMs + effectiveDuration * 3600 * 1000;
      const eventEndMs = etTimeMs(selectedEvent.ends_at);
      const finalEndsMs = Math.min(endsMs, eventEndMs);

      await api.post("/boosts", {
        truck_id: selectedEvent.truck_id || selectedEvent.id,
        starts_at: etWallString(startMs),
        ends_at: etWallString(finalEndsMs),
      });
      toast.success("Boost scheduled");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't schedule boost");
    } finally {
      setSubmitting(false);
    }
  };

  const cancelBoost = async (id) => {
    if (!window.confirm("Cancel this boost?")) return;
    try {
      await api.delete(`/boosts/${id}`);
      toast.success("Boost cancelled");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="boost-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-bold text-primary">
            <Sparkle weight="fill" className="h-3.5 w-3.5" />
            Pro feature
          </div>
          <h3 className="font-display font-semibold text-lg mt-1 flex items-center gap-2">
            <Rocket weight="duotone" className="h-5 w-5 text-primary" />
            Featured boost
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {remaining}/2 boosts remaining this week · up to 12h each, capped to event length
          </p>
        </div>
      </div>

      {events.length === 0 && !loading && (
        <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
          You don't have any upcoming events. Schedule a stop or post a daily, then come back to boost it.
        </div>
      )}

      {events.length > 0 && (
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
              Event to boost
            </label>
            <select
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              className="mt-1 w-full rounded-xl border border-border bg-background px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid="boost-event-select"
            >
              {events.map((e) => {
                // Show a small "📅 GCal" tag for events that came from
                // Google Calendar sync, so owners know "yes I can boost
                // calendar-synced events too" at a glance.
                const fromGcal = e.source === "google_calendar" || !!e.gcal_event_id;
                return (
                  <option key={e.id} value={e.id}>
                    {e.truck_name} · {fmtRange(e.starts_at, e.ends_at)}
                    {fromGcal ? "  📅 GCal" : ""}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
              Duration
            </label>
            <div className="mt-1 flex flex-wrap gap-2">
              {validDurations.map((d) => {
                const active = effectiveDuration === d;
                const label = Number.isInteger(d) ? `${d}h` : `${d}h`;
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDurationH(d)}
                    className={`h-9 px-4 rounded-full text-sm font-medium border transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground border-transparent"
                        : "bg-card border-border hover:bg-muted"
                    }`}
                    data-testid={`boost-duration-${d}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {selectedEvent && validDurations.length === 0 && (
              <p className="mt-1.5 text-xs text-rose-500">
                This event has no time remaining to boost.
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={submit}
            disabled={submitting || !selectedEvent || !effectiveDuration || remaining === 0}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="boost-submit"
          >
            <Lightning weight="fill" className="h-4 w-4" />
            {submitting ? "Scheduling…" : remaining === 0 ? "Out of boosts this week" : "Boost this event"}
          </button>
        </div>
      )}

      {boosts.length > 0 && (
        <div className="pt-3 border-t border-border">
          <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
            Recent boosts
          </h4>
          <ul className="space-y-1.5" data-testid="boost-list">
            {boosts.map((b) => {
              const live = new Date(b.starts_at) <= new Date() && new Date(b.ends_at) >= new Date();
              return (
                <li
                  key={b.id}
                  className="flex items-center justify-between gap-2 text-sm rounded-lg border border-border bg-background/40 px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">{b.truck_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {fmtRange(b.starts_at, b.ends_at)}
                      {live && (
                        <span className="ml-2 inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                          Live
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => cancelBoost(b.id)}
                    className="h-7 w-7 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted"
                    aria-label="Cancel boost"
                    data-testid={`boost-cancel-${b.id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}

function fmtRange(startsIso, endsIso) {
  const day = fmtET(startsIso, { month: "short", day: "numeric" });
  const stime = fmtET(startsIso, { hour: "numeric", minute: "2-digit" });
  const etime = fmtET(endsIso, { hour: "numeric", minute: "2-digit" });
  return `${day} · ${stime}–${etime}`;
}
